import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const LINE_CHART_HEIGHT = 300;
const BAR_CHART_HEIGHT = 250;
const MARGIN = { top: 20, right: 30, bottom: 50, left: 60 };

const ZONE_COLORS = {
  high: { fill: '#dcfce7', label: 'High Sync', threshold: 0.7 },
  moderate: { fill: '#fef9c3', label: 'Moderate', threshold: 0.4 },
  low: { fill: '#fee2e2', label: 'Low Sync', threshold: 0 },
};

const TREND_ARROWS = {
  increasing: { symbol: '\u2191', color: '#16a34a', label: 'Rising' },
  stable: { symbol: '\u2192', color: '#6b7280', label: 'Stable' },
  decreasing: { symbol: '\u2193', color: '#dc2626', label: 'Falling' },
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  overallBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '13px',
    fontWeight: 700,
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginTop: '24px',
    marginBottom: '8px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function getSyncColor(value) {
  if (value >= 0.7) return '#16a34a';
  if (value >= 0.4) return '#f59e0b';
  return '#dc2626';
}

function getSyncBadge(value) {
  if (value >= 0.7) return { bg: '#dcfce7', color: '#166534', label: 'HIGH' };
  if (value >= 0.4) return { bg: '#fef9c3', color: '#854d0e', label: 'MODERATE' };
  return { bg: '#fee2e2', color: '#991b1b', label: 'LOW' };
}

function getBarColor(score) {
  if (score >= 0.7) return '#16a34a';
  if (score >= 0.4) return '#f59e0b';
  return '#dc2626';
}

export default function StateSyncIndex({ data }) {
  const lineSvgRef = useRef(null);
  const barSvgRef = useRef(null);
  const lineContainerRef = useRef(null);
  const barContainerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  // Line chart: sync index over time
  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0 || !data.syncIndex) return;

    const svg = d3.select(lineSvgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = lineContainerRef.current
      ? lineContainerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = LINE_CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', LINE_CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const parsedDates = data.dates.map((d) => new Date(d));

    // Scales
    const x = d3.scaleTime()
      .domain(d3.extent(parsedDates))
      .range([0, innerW]);

    const y = d3.scaleLinear()
      .domain([0, 1])
      .range([innerH, 0]);

    // Color band backgrounds
    // Low zone: 0 to 0.4
    g.append('rect')
      .attr('x', 0)
      .attr('y', y(0.4))
      .attr('width', innerW)
      .attr('height', y(0) - y(0.4))
      .attr('fill', ZONE_COLORS.low.fill)
      .attr('opacity', 0.5);

    // Moderate zone: 0.4 to 0.7
    g.append('rect')
      .attr('x', 0)
      .attr('y', y(0.7))
      .attr('width', innerW)
      .attr('height', y(0.4) - y(0.7))
      .attr('fill', ZONE_COLORS.moderate.fill)
      .attr('opacity', 0.5);

    // High zone: 0.7 to 1.0
    g.append('rect')
      .attr('x', 0)
      .attr('y', y(1))
      .attr('width', innerW)
      .attr('height', y(0.7) - y(1))
      .attr('fill', ZONE_COLORS.high.fill)
      .attr('opacity', 0.5);

    // Zone labels (right side)
    g.append('text')
      .attr('x', innerW - 4)
      .attr('y', y(0.85))
      .attr('text-anchor', 'end')
      .attr('font-size', '10px')
      .attr('fill', '#166534')
      .attr('opacity', 0.7)
      .text('High Sync');

    g.append('text')
      .attr('x', innerW - 4)
      .attr('y', y(0.55))
      .attr('text-anchor', 'end')
      .attr('font-size', '10px')
      .attr('fill', '#854d0e')
      .attr('opacity', 0.7)
      .text('Moderate');

    g.append('text')
      .attr('x', innerW - 4)
      .attr('y', y(0.2))
      .attr('text-anchor', 'end')
      .attr('font-size', '10px')
      .attr('fill', '#991b1b')
      .attr('opacity', 0.7)
      .text('Low Sync');

    // Grid lines
    const yTicks = [0, 0.2, 0.4, 0.6, 0.7, 0.8, 1.0];
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', (d) => (d === 0.4 || d === 0.7) ? '#d1d5db' : '#f3f4f6')
      .attr('stroke-width', (d) => (d === 0.4 || d === 0.7) ? 1 : 1)
      .attr('stroke-dasharray', (d) => (d === 0.4 || d === 0.7) ? '4,3' : 'none');

    // Line
    const line = d3.line()
      .x((d, i) => x(parsedDates[i]))
      .y((d) => y(d))
      .defined((d) => d != null)
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(data.syncIndex)
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', '#2563eb')
      .attr('stroke-width', 2.5);

    // Data point dots
    data.syncIndex.forEach((val, i) => {
      if (val == null) return;
      g.append('circle')
        .attr('cx', x(parsedDates[i]))
        .attr('cy', y(val))
        .attr('r', 4)
        .attr('fill', getSyncColor(val))
        .attr('stroke', '#fff')
        .attr('stroke-width', 2);
    });

    // X axis
    const n = data.dates.length;
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(Math.min(n, 10))
          .tickSize(0)
          .tickFormat((d) => formatDateLabel(d.toISOString()))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-30)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.25em');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .tickValues([0, 0.2, 0.4, 0.6, 0.7, 0.8, 1.0])
          .tickSize(0)
          .tickFormat((v) => v.toFixed(1))
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -45)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .text('Sync Index');

    // Tooltip hover overlay
    const crosshair = g.append('line')
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,3')
      .style('display', 'none');

    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', (event) => {
        const [mx] = d3.pointer(event);
        const bisect = d3.bisector((d) => d).left;
        const dateAtMouse = x.invert(mx);
        const timestamps = parsedDates.map((d) => d.getTime());
        let idx = bisect(timestamps, dateAtMouse.getTime());
        if (idx > 0 && idx < n) {
          const d0 = timestamps[idx - 1];
          const d1 = timestamps[idx];
          idx = (dateAtMouse.getTime() - d0) > (d1 - dateAtMouse.getTime()) ? idx : idx - 1;
        }
        idx = Math.max(0, Math.min(n - 1, idx));

        const cx = x(parsedDates[idx]);
        crosshair
          .attr('x1', cx)
          .attr('x2', cx)
          .style('display', null);

        const val = data.syncIndex[idx];
        let zone = 'Low';
        if (val >= 0.7) zone = 'High';
        else if (val >= 0.4) zone = 'Moderate';

        setTooltip({
          x: event.clientX + 12,
          y: event.clientY - 10,
          type: 'line',
          date: data.dates[idx],
          syncIndex: val,
          zone,
        });
      })
      .on('mouseleave', () => {
        crosshair.style('display', 'none');
        setTooltip(null);
      });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Bar chart: per-state sync scores
  useEffect(() => {
    if (!data || !data.stateScores || data.stateScores.length === 0) return;

    const svg = d3.select(barSvgRef.current);
    svg.selectAll('*').remove();

    const sorted = [...data.stateScores].sort((a, b) => b.avgSync - a.avgSync);

    const barHeight = 24;
    const barGap = 4;
    const labelWidth = 60;
    const trendWidth = 30;
    const rightPad = 20;

    const containerWidth = barContainerRef.current
      ? barContainerRef.current.clientWidth
      : 700;
    const width = Math.max(400, containerWidth);
    const chartLeft = labelWidth;
    const chartRight = width - trendWidth - rightPad;
    const barAreaWidth = chartRight - chartLeft;
    const totalHeight = sorted.length * (barHeight + barGap) + 20;

    svg.attr('width', width).attr('height', totalHeight);

    const g = svg.append('g');

    const x = d3.scaleLinear()
      .domain([0, 1])
      .range([0, barAreaWidth]);

    sorted.forEach((item, i) => {
      const yPos = i * (barHeight + barGap) + 10;

      // State label
      g.append('text')
        .attr('x', labelWidth - 8)
        .attr('y', yPos + barHeight / 2)
        .attr('text-anchor', 'end')
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', 600)
        .attr('fill', '#374151')
        .text(item.state);

      // Background bar
      g.append('rect')
        .attr('x', chartLeft)
        .attr('y', yPos)
        .attr('width', barAreaWidth)
        .attr('height', barHeight)
        .attr('fill', '#f3f4f6')
        .attr('rx', 4);

      // Score bar
      const barW = Math.max(2, x(item.avgSync));
      g.append('rect')
        .attr('x', chartLeft)
        .attr('y', yPos)
        .attr('width', barW)
        .attr('height', barHeight)
        .attr('fill', getBarColor(item.avgSync))
        .attr('fill-opacity', 0.8)
        .attr('rx', 4)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: 'bar',
            state: item.state,
            avgSync: item.avgSync,
            trend: item.trend,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });

      // Score value
      g.append('text')
        .attr('x', chartLeft + barW + 6)
        .attr('y', yPos + barHeight / 2)
        .attr('dominant-baseline', 'middle')
        .attr('font-size', '11px')
        .attr('font-weight', 600)
        .attr('fill', '#4b5563')
        .text(item.avgSync.toFixed(2));

      // Trend arrow
      const trendInfo = TREND_ARROWS[item.trend] || TREND_ARROWS.stable;
      g.append('text')
        .attr('x', chartRight + 14)
        .attr('y', yPos + barHeight / 2)
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'middle')
        .attr('font-size', '14px')
        .attr('font-weight', 700)
        .attr('fill', trendInfo.color)
        .text(trendInfo.symbol);
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.title}>State Price Synchronization</div>
        </div>
        <div style={styles.subtitle}>How coordinated are price movements across states?</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          Not enough data for synchronization analysis
        </div>
      </div>
    );
  }

  const overallBadge = getSyncBadge(data.overallSync);

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div style={styles.title}>State Price Synchronization</div>
        <span
          style={{
            ...styles.overallBadge,
            background: overallBadge.bg,
            color: overallBadge.color,
          }}
        >
          {(data.overallSync * 100).toFixed(0)}% {overallBadge.label}
        </span>
        {data.field && (
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
            ({data.field})
          </span>
        )}
      </div>
      <div style={styles.subtitle}>How coordinated are price movements across states?</div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ width: 12, height: 12, background: ZONE_COLORS.high.fill, border: '1px solid #bbf7d0', borderRadius: 2 }} />
          <span>High Sync (&gt;0.7)</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 12, height: 12, background: ZONE_COLORS.moderate.fill, border: '1px solid #fde68a', borderRadius: 2 }} />
          <span>Moderate (0.4-0.7)</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 12, height: 12, background: ZONE_COLORS.low.fill, border: '1px solid #fecaca', borderRadius: 2 }} />
          <span>Low Sync (&lt;0.4)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2.5" />
          </svg>
          <span>Sync Index</span>
        </div>
      </div>

      {/* Line chart */}
      <div style={styles.container} ref={lineContainerRef}>
        <svg ref={lineSvgRef} />
      </div>

      {/* Per-state bar chart */}
      {data.stateScores && data.stateScores.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Per-State Synchronization Scores</div>
          <div style={styles.container} ref={barContainerRef}>
            <svg ref={barSvgRef} />
          </div>
        </>
      )}

      {/* Pairwise sync table */}
      {data.pairwiseSync && data.pairwiseSync.length > 0 && (
        <>
          <div style={styles.sectionTitle}>Top State Pairs</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {data.pairwiseSync.slice(0, 8).map((pair, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 10px',
                  background: '#f9fafb',
                  borderRadius: '6px',
                  fontSize: '12px',
                }}
              >
                <span style={{ fontWeight: 600, color: '#374151' }}>
                  {pair.state1}-{pair.state2}
                </span>
                <span
                  style={{
                    fontWeight: 700,
                    color: getSyncColor(pair.sync),
                  }}
                >
                  {pair.sync.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          {tooltip.type === 'line' && (
            <>
              <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                {formatDateFull(tooltip.date)}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>Sync Index</span>
                <span style={{ fontWeight: 600, color: getSyncColor(tooltip.syncIndex) }}>
                  {tooltip.syncIndex != null ? tooltip.syncIndex.toFixed(3) : '--'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Zone</span>
                <span style={{ fontWeight: 600, color: getSyncColor(tooltip.syncIndex) }}>
                  {tooltip.zone}
                </span>
              </div>
            </>
          )}
          {tooltip.type === 'bar' && (
            <>
              <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
                {tooltip.state}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
                <span style={{ color: '#6b7280' }}>Avg Sync</span>
                <span style={{ fontWeight: 600, color: getSyncColor(tooltip.avgSync) }}>
                  {tooltip.avgSync.toFixed(3)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <span style={{ color: '#6b7280' }}>Trend</span>
                <span style={{ fontWeight: 600, color: (TREND_ARROWS[tooltip.trend] || TREND_ARROWS.stable).color }}>
                  {(TREND_ARROWS[tooltip.trend] || TREND_ARROWS.stable).symbol} {(TREND_ARROWS[tooltip.trend] || TREND_ARROWS.stable).label}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
